import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Cadillac Escalade (linkId 2383).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2383.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the Cadillac Gothic palette/face verbatim from 2384.
 *
 * Real accessory-grouping bug found and fixed while building this
 * generator (gmc-lib.mjs deriveGroups): Luxury and Sport both have a
 * hidden `default_speaker` accessory excluded by TWO otherwise-unrelated
 * toggles — an audio-system upgrade AND a separate night-vision on/off
 * pair — that don't reference each other. Naive connected-components
 * grouping (correct for every genuine N-way clique found so far, e.g.
 * Yukon's 6-way alloy-wheel set) would have merged these into one wrong
 * 4-way group. Fixed generally: a hub is only treated as one real
 * multi-way group if its neighbours are mutually reachable without it
 * (a clique/chain); otherwise only its first (lowest-key) edge is kept
 * and the rest — Night Vision here — form their own separate group.
 * Verified: {default_speaker, Audio System} and {WO_NIGHT_VISION, Night
 * Vision} now correctly land as two independent groups on both trims.
 *
 * Dropped: 1.0's `engine`/`transmission` filterOptions (generic
 * Petrol/Turbo Petrol, CVT/MT/AMT boilerplate found across most GM projects
 * so far, unrelated to any real trim distinction here). `toggleTabs` hides
 * CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2383/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Escalade: {
      variants: {
        luxury: {
          configurationId: 's908pa12509ehwdd44',
          name: {
            en: 'Luxury',
            ar: 'الفئة الفخامة',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'Luxury',
          highlights: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: 'Torque 623.7 NM',
              ar: 'عزم الدوران 623.7 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'محرك V8 6.2 لتر',
            },
          ],
          specs: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: '623.7 NM',
              ar: '623.7 نيوتن متر',
            },
          ],
          image: 'images/2383/variant_lux.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SB',
          brandItemCode: 'CAES01',
        },
        sport: {
          configurationId: 's8na12509ehwdd44',
          name: {
            en: 'Sport',
            ar: 'الفئة الرياضية',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'Sport',
          highlights: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: 'Torque 623.7 NM',
              ar: 'عزم الدوران 623.7 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'محرك V8 6.2 لتر',
            },
          ],
          specs: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: '623.7 NM',
              ar: '623.7 نيوتن متر',
            },
          ],
          image: 'images/2383/variant_sport.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SC',
          brandItemCode: 'CAES03',
        },
        'platinum-luxury': {
          configurationId: 's8na125e09hwdd44',
          name: {
            en: 'Platinum Luxury',
            ar: 'فئة البلاتينوم الفاخرة',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'Platinum Luxury',
          highlights: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: 'Torque 623.7 NM',
              ar: 'عزم الدوران 623.7 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'محرك V8 6.2 لتر',
            },
          ],
          specs: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: '623.7 NM',
              ar: '623.7 نيوتن متر',
            },
          ],
          image: 'images/2383/variant_plat_lux.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SG',
          brandItemCode: 'CAES05',
        },
        'platinum-sport': {
          configurationId: 's8na125ehwdd44',
          name: {
            en: 'Platinum Sport',
            ar: 'فئة البلاتينوم الرياضية',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'Platinum Sport',
          highlights: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: 'Torque 623.7 NM',
              ar: 'عزم الدوران 623.7 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'محرك V8 6.2 لتر',
            },
          ],
          specs: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: '623.7 NM',
              ar: '623.7 نيوتن متر',
            },
          ],
          image: 'images/2383/variant_plat_sport.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SH',
          brandItemCode: 'CAES04',
        },
        'v-series': {
          configurationId: 's8na5ehwdd44',
          name: {
            en: 'V - Series',
            ar: 'فئة V-Series',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'V - Series',
          highlights: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: 'Torque 623.7 NM',
              ar: 'عزم الدوران 623.7 نيوتن متر',
            },
            {
              en: 'V8 Engine 6.2L',
              ar: 'محرك V8 6.2 لتر',
            },
          ],
          specs: [
            {
              en: 'Up to 682 HP',
              ar: 'حتى 682 حصاناً',
            },
            {
              en: '623.7 NM',
              ar: '623.7 نيوتن متر',
            },
          ],
          image: 'images/2383/variant_escalade_v.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 4,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SD',
          brandItemCode: 'CAES06',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2383/ext_colors/official/Black_Raven.jpg',
          title: {
            en: 'Landscape 1',
            ar: 'المشهد 1',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2383/CitySample_2.png',
          title: {
            en: 'Landscape 2',
            ar: 'المشهد 2',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          '30ja6fs1si26': {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2383/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          '1d0y9evbmngc': {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          mwsv1fnhg5wz: {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          onwrm9wz90ia: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر ساطع بطبقتين',
            },
            image: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          wqo8160kpmns: {
            label: {
              en: 'Galactic Gray Metallic',
              ar: 'رمادي Galactic معدني',
            },
            image: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX75',
          },
          bpsnuu0maoxi: {
            label: {
              en: 'Aegean Stone',
              ar: 'Aegean Stone',
            },
            image: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX54',
          },
          w2x9zru8b9md: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2383/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          oo9of93p42zt: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          mkosdhfocbor: {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          woq5tg300942: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر ساطع بطبقتين',
            },
            image: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          m7whgsqs0reu: {
            label: {
              en: 'Galactic Gray Metallic',
              ar: 'رمادي Galactic معدني',
            },
            image: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX75',
          },
          bpsnuopu0maoxi: {
            label: {
              en: 'Aegean Stone',
              ar: 'Aegean Stone',
            },
            image: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX54',
          },
          '1x3l0pzugt5o': {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2383/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          de8yhyoz7ilw: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          n8kjkv3l71rj: {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          umw4uekyrr6c: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر ساطع بطبقتين',
            },
            image: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '48k36mijbi84': {
            label: {
              en: 'Galactic Gray Metallic',
              ar: 'رمادي Galactic معدني',
            },
            image: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX75',
          },
          lczhp0lkw9x5: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2383/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          ean8osok96bg: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          '4nkqa7bbmps8': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          uqziwot9305o: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر ساطع بطبقتين',
            },
            image: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          hrm6fvne1q87: {
            label: {
              en: 'Galactic Gray Metallic',
              ar: 'رمادي Galactic معدني',
            },
            image: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Galactic_Gray_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX75',
          },
          imj5i4p0mwho: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2383/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          '9zzwwce8hhvm': {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          m1n06yw87mjc: {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 24,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          z10htvrc4695: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر ساطع بطبقتين',
            },
            image: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 25,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          t0l2tlj9fv25: {
            label: {
              en: 'Aegean Stone',
              ar: 'Aegean Stone',
            },
            image: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 26,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2383/ext_colors/official/Aegean_Stone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX54',
          },
        },
        interior: {
          '03wk11dwf99s': {
            label: {
              en: 'Jet Black with Perforated inserts',
              ar: 'Jet Black مع تطعيمات مثقبة',
            },
            image: 'images/2383/int_colors/official/Jet_Black_Perforated.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Jet_Black_Perforated.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H2U',
            brandItemCode: 'IN55',
          },
          j50tistnihpo: {
            label: {
              en: 'Brandy with Very Dark Atmosphere accents',
              ar: 'Brandy مع لمسات باللون Very Dark Atmosphere',
            },
            image: 'images/2383/int_colors/official/Brandy.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Brandy.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EE4',
            brandItemCode: 'IN39',
          },
          j50tistnihp1: {
            label: {
              en: 'Renaissance Red with Jet Black Accents',
              ar: 'Renaissance Red مع لمسات باللون Jet Black',
            },
            image: 'images/2383/int_colors/official/Renaissance_Red.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Renaissance_Red.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EH3',
            brandItemCode: 'IN71',
          },
          '85nvtwjjtuim': {
            label: {
              en: 'Jet Black with Perforated inserts',
              ar: 'Jet Black مع تطعيمات مثقبة',
            },
            image: 'images/2383/int_colors/official/Jet_Black_Perforated.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Jet_Black_Perforated.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H2U',
            brandItemCode: 'IN55',
          },
          ww5be0hikbdt: {
            label: {
              en: 'Whisper Beige with Jet Black accents',
              ar: 'Whisper Beige مع لمسات باللون الأسود Jet Black',
            },
            image: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H0D',
            brandItemCode: 'IN83',
          },
          vvk04t5qc25i: {
            label: {
              en: 'Renaissance Red with Jet Black Accents',
              ar: 'Renaissance Red مع لمسات باللون Jet Black',
            },
            image: 'images/2383/int_colors/official/Renaissance_Red.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Renaissance_Red.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EH3',
            brandItemCode: 'IN71',
          },
          h0cuksv2pqic: {
            label: {
              en: 'Jet Black',
              ar: 'Jet Black',
            },
            image: 'images/2383/int_colors/official/Jet_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Jet_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HIZ',
            brandItemCode: 'IN49',
          },
          x6jha35ah86k: {
            label: {
              en: 'Sheer Gray with Jet Black accents',
              ar: 'Sheer Gray مع لمسات باللون الأسود Jet Black',
            },
            image: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EH5',
            brandItemCode: 'IN74',
          },
          do54iqbddf9v: {
            label: {
              en: 'Whisper Beige with Gideon accents',
              ar: 'Whisper Beige مع لمسات باللون Gideon',
            },
            image: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 8,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H0D',
            brandItemCode: 'IN82',
          },
          w2cu8xd8vbzl: {
            label: {
              en: 'Jet Black',
              ar: 'Jet Black',
            },
            image: 'images/2383/int_colors/official/Jet_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 9,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Jet_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HIZ',
            brandItemCode: 'IN49',
          },
          nl6ui3aic9te: {
            label: {
              en: 'Sheer Gray with Jet Black accents',
              ar: 'Sheer Gray مع لمسات باللون الأسود Jet Black',
            },
            image: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 10,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EH5',
            brandItemCode: 'IN74',
          },
          '7s7c7qz71764': {
            label: {
              en: 'Whisper Beige with Gideon accents',
              ar: 'Whisper Beige مع لمسات باللون Gideon',
            },
            image: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 11,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Whisper_Beige.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H0D',
            brandItemCode: 'IN82',
          },
          pcy4nlxr756u: {
            label: {
              en: 'Jet Black',
              ar: 'Jet Black',
            },
            image: 'images/2383/int_colors/official/Jet_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 12,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Jet_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'HIZ',
            brandItemCode: 'IN49',
          },
          ys9drkq7y7ju: {
            label: {
              en: 'Sheer Gray with Jet Black accents',
              ar: 'Sheer Gray مع لمسات باللون الأسود Jet Black',
            },
            image: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 13,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2383/int_colors/official/Sheer_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EH5',
            brandItemCode: 'IN74',
          },
        },
      },
      features: {
        '50fyijsa8h88': {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2383/lux_Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '1e0q63itjg52': {
          label: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          image: 'images/2383/lux_Suspension.jpg',
          title: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        x5p4553jsccd: {
          label: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          image: 'images/2383/lux_Slip_Differential.jpg',
          title: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        x5p450953jsccd: {
          label: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          image: 'images/2383/lux_Illuminated_Logo.jpg',
          title: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        sdkeibj4zedx: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2383/lux_Infotainment_Screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        '9v7g6wt62yu6': {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2383/lux_Command_Center.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        g6h9z7ho9usu: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2383/lux_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        ja33evostgzo4: {
          label: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          image: 'images/2383/lux_Super_Cruise.jpg',
          title: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          desc: {
            en: 'Hands-free driver assistance technology with 3-year OnStar® One Plan',
            ar: 'تقنية مساعدة السائق من دون استخدام اليدين مع باقة اونستار One لمدة 3 سنوات',
          },
          type: 'interior',
        },
        ja35evostgzo4: {
          label: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          image: 'images/2383/lux_Headup_Display.jpg',
          title: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          desc: {
            en: 'The available Full-Color Head-Up Display feature is designed to help you keep your eyes on the road by projecting key information directly onto the windshield or onto a small screen in front of the windshield, low in your field of vision',
            ar: 'صُممت شاشة العرض الأمامية الملونة بالكامل المتوفرة لمساعدتك على إبقاء عينيك على الطريق، من خلال عرض المعلومات الأساسية مباشرةً على الزجاج الأمامي أو على شاشة صغيرة أمام الزجاج الأمامي ضمن مجال رؤيتك.',
          },
          type: 'interior',
        },
        q7lhy68om5cb: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2383/sport_Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '6ysctwifu1yb': {
          label: {
            en: 'Sport grille',
            ar: 'الشبك الرياضي',
          },
          image: 'images/2383/Sport_Grille.jpg',
          title: {
            en: 'Sport grille',
            ar: 'الشبك الرياضي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        cu0f7ese0i1o: {
          label: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          image: 'images/2383/sport_Suspension.jpg',
          title: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '8ptzflr2c84c': {
          label: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          image: 'images/2383/sport_Slip_Differential.jpg',
          title: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '2gn5w9aahsu1': {
          label: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          image: 'images/2383/sport_Illuminated_Logo.jpg',
          title: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        hpj0bzml4qf6: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2383/sport_Infotainment_Screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        knb6d6ov609e: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2383/sport_Monitoring.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        e097g5qkjv2c: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2383/sport_Command_Center.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        hfv7nd2hrk5v: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2383/sport_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        irh0u7svt9w8: {
          label: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          image: 'images/2383/sport_Super_Cruise.jpg',
          title: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          desc: {
            en: 'Hands-free driver assistance technology with 3-year OnStar® One Plan',
            ar: 'تقنية مساعدة السائق من دون استخدام اليدين مع باقة اونستار One لمدة 3 سنوات',
          },
          type: 'interior',
        },
        '2vl8wmotu9jo': {
          label: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          image: 'images/2383/sport_Headup_Display.jpg',
          title: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          desc: {
            en: 'The available Full-Color Head-Up Display feature is designed to help you keep your eyes on the road by projecting key information directly onto the windshield or onto a small screen in front of the windshield, low in your field of vision',
            ar: 'صُممت شاشة العرض الأمامية الملونة بالكامل المتوفرة لمساعدتك على إبقاء عينيك على الطريق، من خلال عرض المعلومات الأساسية مباشرةً على الزجاج الأمامي أو على شاشة صغيرة أمام الزجاج الأمامي ضمن مجال رؤيتك.',
          },
          type: 'interior',
        },
        biwfr75k1usd: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2383/plat_lux_Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '0y6yp2hp5d52': {
          label: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          image: 'images/2383/plat_lux_Suspension.jpg',
          title: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        wd5y4twqd1ig: {
          label: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          image: 'images/2383/plat_lux_Slip_Differential.jpg',
          title: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        dy5tr3bmtzeg: {
          label: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          image: 'images/2383/plat_lux_Illuminated_Logo.jpg',
          title: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        tq0lmsblvr5q: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2383/plat_lux_Infotainment_Screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        z6tytr3ven4z: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2383/plat_lux_Sound_System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        kbnsp4u7me10: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2383/plat_lux_Command_Center.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        u3css7vd0vwi: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2383/plat_lux_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        kxjqj2jvqqtx: {
          label: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          image: 'images/2383/plat_lux_Entertainment_System.jpg',
          title: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          desc: {
            en: 'Rear seat entertainment system',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          type: 'interior',
        },
        tzrpfdz82u9h: {
          label: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          image: 'images/2383/plat_lux_Super_Cruise.jpg',
          title: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          desc: {
            en: 'Hands-free driver assistance technology with 3-year OnStar® One Plan',
            ar: 'تقنية مساعدة السائق من دون استخدام اليدين مع باقة اونستار One لمدة 3 سنوات',
          },
          type: 'interior',
        },
        cf97wd6fshm9: {
          label: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          image: 'images/2383/plat_lux_Headup_Display.jpg',
          title: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          desc: {
            en: 'The available Full-Color Head-Up Display feature is designed to help you keep your eyes on the road by projecting key information directly onto the windshield or onto a small screen in front of the windshield, low in your field of vision',
            ar: 'صُممت شاشة العرض الأمامية الملونة بالكامل المتوفرة لمساعدتك على إبقاء عينيك على الطريق، من خلال عرض المعلومات الأساسية مباشرةً على الزجاج الأمامي أو على شاشة صغيرة أمام الزجاج الأمامي ضمن مجال رؤيتك.',
          },
          type: 'interior',
        },
        '26yhehm42yz1': {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2383/plat_sport_Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        pjcx8ghbj12t: {
          label: {
            en: 'Sport grille',
            ar: 'الشبك الرياضي',
          },
          image: 'images/2383/plat_sport_Sport_Grille.jpg',
          title: {
            en: 'Sport grille',
            ar: 'الشبك الرياضي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        ppv43v19fgn2: {
          label: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          image: 'images/2383/plat_sport_Suspension.jpg',
          title: {
            en: 'Air Ride Adaptive Suspension',
            ar: 'نظام التعليق الهوائي المتكيف Air Ride',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        m5ueop90f4sh: {
          label: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          image: 'images/2383/plat_sport_Slip_Differential.jpg',
          title: {
            en: 'Electronic Limited-Slip Differential',
            ar: 'ترس تفاضلي إلكتروني محدود الانزلاق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        hihm6d5nyshp: {
          label: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          image: 'images/2383/plat_sport_Illuminated_Logo.jpg',
          title: {
            en: 'Illuminated Crest',
            ar: 'شعار كاديلاك المضيء',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        fwiqhrtu5nm1: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2383/plat_sport_Infotainment_Screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        lg9x6hx64lar: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2383/plat_sport_Sound_System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        '21ms0e46yfck': {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2383/plat_sport_Command_Center.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        '89wja0hrqis0': {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2383/plat_sport_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        '4dqov71oe70l': {
          label: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          image: 'images/2383/plat_sport_Entertainment_System.jpg',
          title: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          desc: {
            en: 'Rear seat entertainment system',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          type: 'interior',
        },
        '1zfbkmer88eh': {
          label: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          image: 'images/2383/plat_sport_Super_Cruise.jpg',
          title: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          desc: {
            en: 'Hands-free driver assistance technology with 3-year OnStar® One Plan',
            ar: 'تقنية مساعدة السائق من دون استخدام اليدين مع باقة اونستار One لمدة 3 سنوات',
          },
          type: 'interior',
        },
        vw7ix18goypx: {
          label: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          image: 'images/2383/plat_sport_Headup_Display.jpg',
          title: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          desc: {
            en: 'The available Full-Color Head-Up Display feature is designed to help you keep your eyes on the road by projecting key information directly onto the windshield or onto a small screen in front of the windshield, low in your field of vision',
            ar: 'صُممت شاشة العرض الأمامية الملونة بالكامل المتوفرة لمساعدتك على إبقاء عينيك على الطريق، من خلال عرض المعلومات الأساسية مباشرةً على الزجاج الأمامي أو على شاشة صغيرة أمام الزجاج الأمامي ضمن مجال رؤيتك.',
          },
          type: 'interior',
        },
        ejzd2tssw6fp: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2383/esc_v_Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'The Escalade V-Series features a bold design with an illuminated grille, vertical LED lighting, power doors, and 24" wheel, complemented by striking rear styling for a stronger road presence.',
            ar: 'يتميز طراز Escalade V-Series بتصميم جريء مع شبك أمامي مضاء، وإضاءة LED عمودية، وأبواب كهربائية، وعجلات قياس 24 بوصة، ويكتمل ذلك بتصميم خلفي لافت يمنحه حضوراً أقوى على الطريق.',
          },
          type: 'exterior',
        },
        up00tngp5v1o: {
          label: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          image: 'images/2383/esc_v_Front_grille.jpg',
          title: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          desc: {
            en: 'High Gloss Black Finishes, Illuminated Grille Surround and Unique Fascias',
            ar: 'تشطيبات باللون الأسود شديد اللمعان، وإطار مضاء للشبك الأمامي، وواجهات أمامية وخلفية بتصميم فريد',
          },
          type: 'exterior',
        },
        habfgb6fu2k2: {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2383/esc_v_Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: '24" Fully painted satin graphite with laser etching (R5D)',
            ar: 'عجلات قياس 24 بوصة مطلية بالكامل بتشطيب Satin Graphite مع نقش بالليزر (R5D)',
          },
          type: 'exterior',
        },
        bk25r28rt6y0: {
          label: {
            en: 'Rear Fascia',
            ar: 'الواجهة الخلفية',
          },
          image: 'images/2383/esc_v_Rear_Fascia.jpg',
          title: {
            en: 'Rear Fascia',
            ar: 'الواجهة الخلفية',
          },
          desc: {
            en: 'The V-Series rear design features a bold rear fascia and unique exhaust, along with a license plate applique and a distinctive taillamp signature.',
            ar: 'يتميز التصميم الخلفي لفئة V-Series بواجهة خلفية جريئة، ونظام عادم بتصميم فريد، ولوحة تزيين لمكان لوحة الأرقام، إلى جانب بصمة مميزة للمصابيح الخلفية.',
          },
          type: 'exterior',
        },
        ljxlvsxjaks3: {
          label: {
            en: 'Power Doors',
            ar: 'الأبواب الكهربائية',
          },
          image: 'images/2383/esc_v_Power_Doors.jpg',
          title: {
            en: 'Power Doors',
            ar: 'الأبواب الكهربائية',
          },
          desc: {
            en: 'Power open and close doors',
            ar: 'أبواب كهربائية للفتح والإغلاق',
          },
          type: 'interior',
        },
        pxffbvox76hj: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2383/esc_v_Infotainment_screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        ahgeo58xhzrf: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2383/esc_v_Sound_System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        ja3opivostgzo4: {
          label: {
            en: 'Steering Wheel',
            ar: 'عجلة القيادة',
          },
          image: 'images/2383/esc_v_Steering_Wheel.jpg',
          title: {
            en: 'Steering Wheel',
            ar: 'عجلة القيادة',
          },
          desc: {
            en: 'Bold Steering wheel design',
            ar: 'تصميم جريء لعجلة القيادة',
          },
          type: 'interior',
        },
        jhzoo1cys8wj: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2383/esc_v_Command_Center.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        vv7soximywgs: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2383/esc_v_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        '4d2gtev6hchw': {
          label: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          image: 'images/2383/esc_v_Entertainment_System.jpg',
          title: {
            en: 'Entertainment System',
            ar: 'نظام الترفيه',
          },
          desc: {
            en: 'Rear seat entertainment system',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          type: 'interior',
        },
        vep0tetjgaz6: {
          label: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          image: 'images/2383/vseries_Super_Cruise.jpg',
          title: {
            en: 'Standard Super-Cruise',
            ar: 'نظام Super Cruise القياسي',
          },
          desc: {
            en: 'Hands-free driver assistance technology with 3-year OnStar® One Plan',
            ar: 'تقنية مساعدة السائق من دون استخدام اليدين مع باقة اونستار One لمدة 3 سنوات',
          },
          type: 'interior',
        },
        ya2qkgvmvel8: {
          label: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          image: 'images/2383/vseries_Headup_Display.jpg',
          title: {
            en: 'Full Color Head-Up Display',
            ar: 'شاشة عرض أمامية ملونة بالكامل',
          },
          desc: {
            en: 'The available Full-Color Head-Up Display feature is designed to help you keep your eyes on the road by projecting key information directly onto the windshield or onto a small screen in front of the windshield, low in your field of vision',
            ar: 'صُممت شاشة العرض الأمامية الملونة بالكامل المتوفرة لمساعدتك على إبقاء عينيك على الطريق، من خلال عرض المعلومات الأساسية مباشرةً على الزجاج الأمامي أو على شاشة صغيرة أمام الزجاج الأمامي ضمن مجال رؤيتك.',
          },
          type: 'interior',
        },
      },
      accessories: {
        gdnmye26fu4u: {
          label: {
            en: 'Color_based_default_accessory',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8ylilfy10w5s': {
          label: {
            en: 'Variant_Parts_Luxury',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8yrlilfy10w5s': {
          label: {
            en: 'WO_Touring_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8yrllilfy10w5s': {
          label: {
            en: 'default_speaker',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8yrplilfy10w5s': {
          label: {
            en: 'seats_default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8yrkolilfy10w5s': {
          label: {
            en: 'R4E_22in',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'R4E',
        },
        '8yrkolillpfy10w5s': {
          label: {
            en: '10-Spoke alloy, Polished finish with High Gloss Black and laser etching',
            ar: 'عجلات معدنية بتصميم ذي 10 أذرع، مع تشطيب Polished، ولمسات باللون الأسود شديد اللمعان، ونقش بالليزر',
          },
          image: 'images/2383/lux_RF7.jpg',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RF7',
          brandItemCode: 'AC129',
        },
        '8yrplinhllfy10w5s': {
          label: {
            en: 'Bench Seats, second row 60/40 split-folding',
            ar: 'مقاعد الصف الثاني بنظام المقعد المتصل، قابلة للطي بنسبة 60/40',
          },
          image: 'images/2383/lux_Bench_Seats.jpg',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ATT',
          brandItemCode: 'AC152',
        },
        '8yrkolklpillpfy10w5s': {
          label: {
            en: 'Touring Package',
            ar: 'باقة Touring',
          },
          image: 'images/2383/lux_W_Touring_Package.jpg',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ST1',
          brandItemCode: 'AC226',
        },
        '8rtrkolklpillpfy10w5s': {
          label: {
            en: 'Audio system feature, AKG Studio Reference 38-speaker system',
            ar: 'نظام AKG Studio Reference الصوتي المزوّد بـ38 مكبر صوت',
          },
          image: 'images/2383/lux_W_38_SPEAKER_DOORS.jpg',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UQH',
          brandItemCode: 'AC147',
        },
        '90rrkokfhfbpillpfy10w5s': {
          label: {
            en: 'WO_NIGHT_VISION',
            ar: 'الرؤية الليلية',
          },
          image: '',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8rtrkokfhfbpillpfy10w5s': {
          label: {
            en: 'Night Vision',
            ar: 'الرؤية الليلية',
          },
          image: 'images/2383/lux_W_NIGHT_VISION.jpg',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UV3',
          brandItemCode: 'OP30',
        },
        '8rtasdfgbpillpfy10w5s': {
          label: {
            en: 'CONSOLE_REAR_1_WO_HDMI',
            ar: 'الكونسول الخلفي',
          },
          image: '',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8rklpoiujbpillpfy10w5s': {
          label: {
            en: 'Rear Seat Entertainment System Black',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: 'images/2383/lux_Entertainment_System.jpg',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC205',
        },
        zu5m5sb9t81m: {
          label: {
            en: 'Color_based_default_accessory',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        k0h4maktx6fu: {
          label: {
            en: 'Variant_Parts_Sport',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        jusz70vfg3wf: {
          label: {
            en: 'WO_Touring_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        of42f2v2yhk4: {
          label: {
            en: 'WO_38_SPEAKER_Black',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '12a5c5qeaf6c': {
          label: {
            en: 'seats_default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        rq4hxnft62j7: {
          label: {
            en: 'R4F 22in',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'R4F',
        },
        dncn7hz4g6kt: {
          label: {
            en: '7-Spoke alloy with Pearl Nickel and High Gloss Black insert',
            ar: 'عجلات معدنية بتصميم ذي 7 أذرع مع تشطيب Pearl Nickel وتطعيمات باللون الأسود شديد اللمعان',
          },
          image: 'images/2383/sport_RF5.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RF5',
          brandItemCode: 'AC141',
        },
        '8yrkopolillpfy10w5s': {
          label: {
            en: 'Onyx Package',
            ar: 'باقة Onyx',
          },
          image: 'images/2383/sport_SSX_22in.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'PDB',
          brandItemCode: 'AC198',
        },
        f6fvegzexe88: {
          label: {
            en: 'Touring Package',
            ar: 'باقة Touring',
          },
          image: 'images/2383/sport_W_Touring_Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ST1',
          brandItemCode: 'AC226',
        },
        '6yw6n1ws9p68': {
          label: {
            en: 'Bench Seats, second row 60/40 split-folding',
            ar: 'مقاعد الصف الثاني بنظام المقعد المتصل، قابلة للطي بنسبة 60/40',
          },
          image: 'images/2383/sport_Bench_Seats.jpg',
          category: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ATT',
          brandItemCode: 'AC152',
        },
        '4sls1qj6n5ck': {
          label: {
            en: 'Audio system feature, AKG Studio Reference 38-speaker system',
            ar: 'نظام AKG Studio Reference الصوتي المزوّد بـ38 مكبر صوت',
          },
          image: 'images/2383/sport_W_38_SPEAKER_DOORS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UQH',
          brandItemCode: 'AC147',
        },
        tg03zsnqhgwn: {
          label: {
            en: 'WO_NIGHT_VISION',
            ar: 'الرؤية الليلية',
          },
          image: '',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        xuxf5pk3xwud: {
          label: {
            en: 'Night Vision',
            ar: 'الرؤية الليلية',
          },
          image: 'images/2383/sport_W_NIGHT_VISION.jpg',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UV3',
          brandItemCode: 'OP30',
        },
        wn6eoge8afkm: {
          label: {
            en: 'CONSOLE_REAR_1_WO_HDMI',
            ar: 'الكونسول الخلفي',
          },
          image: '',
          category: {
            en: 'Options',
            ar: 'الخيارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        pysqjeg9tgf8: {
          label: {
            en: 'Rear Seat Entertainment System Black',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: 'images/2383/sport_Entertainment_System.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC205',
        },
        btyd2soc1zlw: {
          label: {
            en: 'Color_based_default_accessory',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        nxnxvn7d8c3r: {
          label: {
            en: 'Variant_Parts_Platinum_Luxury',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        qj6dxatn057y: {
          label: {
            en: 'CONSOLE_REAR_1_W_HDMI',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '51ruk1agnbdv': {
          label: {
            en: 'W_NIGHT_VISION',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UV3',
        },
        lrhbknsfx974: {
          label: {
            en: 'seats_default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ndt94xrjumar: {
          label: {
            en: 'R4D 22in',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'R4D',
        },
        f8lunm5xjxo4: {
          label: {
            en: '10-Spoke alloy, Polished finish with High Gloss Black and laser etching',
            ar: 'عجلات معدنية بتصميم ذي 10 أذرع، مع تشطيب Polished، ولمسات باللون الأسود شديد اللمعان، ونقش بالليزر',
          },
          image: 'images/2383/plat_lux_RF7.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RF7',
          brandItemCode: 'AC129',
        },
        gfd5nv4niood: {
          label: {
            en: 'Executive Second Row Seating Package',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2383/plat_lux_W_Executive_Seets.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ABV',
          brandItemCode: 'AC174',
        },
        ce6nckeukfx3: {
          label: {
            en: 'Color_based_default_accessory',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        dd3ht5t32etq: {
          label: {
            en: 'Variant_Parts_Platinum_Sport',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        c8xqfoz981wp: {
          label: {
            en: 'CONSOLE_REAR_1_W_HDMI',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        lypvdd7yyj0l: {
          label: {
            en: 'W_NIGHT_VISION',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UV3',
        },
        w2tf0r4a2knz: {
          label: {
            en: 'seats_default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        z837kwy9wls0: {
          label: {
            en: 'SMM 22in',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'SMM',
        },
        i4dnswxc0d90: {
          label: {
            en: '7-Spoke alloy with Pearl Nickel and High Gloss Black insert',
            ar: 'عجلات معدنية بتصميم ذي 7 أذرع مع تشطيب Pearl Nickel وتطعيمات باللون الأسود شديد اللمعان',
          },
          image: 'images/2383/plat_sport_RF5.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RF5',
          brandItemCode: 'AC141',
        },
        '8glefqypdy46': {
          label: {
            en: 'Onyx Package',
            ar: 'باقة Onyx',
          },
          image: 'images/2383/plat_sport_SSX_22in.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'PDB',
          brandItemCode: 'AC198',
        },
        fiu9a1c8ir5n: {
          label: {
            en: 'Executive Second Row Seating Package',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2383/plat_sport_W_Executive_Seets.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ABV',
          brandItemCode: 'AC174',
        },
        '2gxjds87y443': {
          label: {
            en: 'Color_based_default_accessory',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        uioaga6go12e: {
          label: {
            en: 'Variant_Parts_V_Series',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        o4wwlrl3fven: {
          label: {
            en: 'W_38_SPEAKER_Black',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UQH',
        },
        x9dce7dvd85p: {
          label: {
            en: 'CONSOLE_REAR_1_W_HDMI',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '0s7bm9k1h7vq': {
          label: {
            en: 'Rear_Seat_Entertainment_System_Black',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC205',
        },
        j1eat5kj0b9e: {
          label: {
            en: 'W_NIGHT_VISION',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UV3',
        },
        '4v195on8wcf4': {
          label: {
            en: 'ALLOY_R5D 24in',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: '',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'R5D',
        },
      },
      packages: {
        '8yrkopolillpfy10w5s': {
          label: {
            en: 'Onyx Package',
            ar: 'باقة Onyx',
          },
          image: 'images/2383/sport_SSX_22in.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Onyx Package includes 22" Multi-Spoke Gloss Black wheels and Black exterior accents.',
            ar: 'تتضمن باقة Onyx عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع وتطعيمات خارجية سوداء.',
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDB',
          accessoryContent: [
            {
              accessoryTitle: {
                en: '22" Multi-Spoke Gloss Black wheels',
                ar: 'عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع',
              },
              accessoryDescription: {
                en: '22" Multi-Spoke Gloss Black wheels',
                ar: 'عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع',
              },
              image: 'images/2383/sport_SSX_22in.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          brandItemCode: 'AC198',
        },
        f6fvegzexe88: {
          label: {
            en: 'Touring Package',
            ar: 'باقة Touring',
          },
          image: 'images/2383/sport_W_Touring_Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Touring Package includes enhanced seating and interior comfort features.',
            ar: 'تتضمن باقة Touring مقاعد محسّنة وميزات راحة داخلية.',
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'ST1',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Touring Package interior features',
                ar: 'ميزات باقة Touring الداخلية',
              },
              accessoryDescription: {
                en: 'Touring Package interior features',
                ar: 'ميزات باقة Touring الداخلية',
              },
              image: 'images/2383/sport_W_Touring_Package.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          brandItemCode: 'AC226',
        },
        gfd5nv4niood: {
          label: {
            en: 'Executive Second Row Seating Package',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2383/plat_lux_W_Executive_Seets.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Executive Second Row Seating Package includes executive seating with premium materials and comfort features.',
            ar: 'تتضمن باقة المقاعد التنفيذية للصف الثاني مقاعد تنفيذية بمواد فاخرة وميزات راحة.',
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'ABV',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Executive second row seating',
                ar: 'المقاعد التنفيذية للصف الثاني',
              },
              accessoryDescription: {
                en: 'Executive second row seating with premium materials',
                ar: 'المقاعد التنفيذية للصف الثاني بمواد فاخرة',
              },
              image: 'images/2383/plat_lux_W_Executive_Seets.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          brandItemCode: 'AC174',
        },
        '8glefqypdy46': {
          label: {
            en: 'Onyx Package',
            ar: 'باقة Onyx',
          },
          image: 'images/2383/plat_sport_SSX_22in.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Onyx Package includes 22" Multi-Spoke Gloss Black wheels and Black exterior accents.',
            ar: 'تتضمن باقة Onyx عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع وتطعيمات خارجية سوداء.',
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'PDB',
          accessoryContent: [
            {
              accessoryTitle: {
                en: '22" Multi-Spoke Gloss Black wheels',
                ar: 'عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع',
              },
              accessoryDescription: {
                en: '22" Multi-Spoke Gloss Black wheels',
                ar: 'عجلات قياس 22 بوصة متعددة الأذرع باللون الأسود اللامع',
              },
              image: 'images/2383/plat_sport_SSX_22in.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          brandItemCode: 'AC198',
        },
        fiu9a1c8ir5n: {
          label: {
            en: 'Executive Second Row Seating Package',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2383/plat_sport_W_Executive_Seets.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Executive Second Row Seating Package includes executive seating with premium materials and comfort features.',
            ar: 'تتضمن باقة المقاعد التنفيذية للصف الثاني مقاعد تنفيذية بمواد فاخرة وميزات راحة.',
          },
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'ABV',
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Executive second row seating',
                ar: 'المقاعد التنفيذية للصف الثاني',
              },
              accessoryDescription: {
                en: 'Executive second row seating with premium materials',
                ar: 'المقاعد التنفيذية للصف الثاني بمواد فاخرة',
              },
              image: 'images/2383/plat_sport_W_Executive_Seets.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          brandItemCode: 'AC174',
        },
      },
      accessoryCategories: {
        exterior: ['Alloy Wheels'],
        interior: ['Seats'],
        options: ['Accessories', 'Options', 'Options & Accessories'],
      },
      interactions: {
        ihmz2ch7lru2: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '53ehrrkf0yxy': {
          label: {
            en: 'Boot',
            ar: 'صندوق الأمتعة',
          },
          type: 'interaction',
        },
        rc1z7dwmbfif: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        w3yjdzjao2i0: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        toijdriu1av5: {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية',
          },
          type: 'interaction',
        },
        g64qit5yzfp2: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        '8xaasoxlyubu': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        wkva8yld7j1a: {
          label: {
            en: 'Boot',
            ar: 'صندوق الأمتعة',
          },
          type: 'interaction',
        },
        pk3dhqpkibjd: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        '7xkjflfgf38q': {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        ppv2p9fprbyu: {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية',
          },
          type: 'interaction',
        },
        yhn65g05fv3l: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        '6gohvikpcnsq': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        j33lsupbq8po: {
          label: {
            en: 'Boot',
            ar: 'صندوق الأمتعة',
          },
          type: 'interaction',
        },
        '9a7dr6gbgr8l': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        '0hweglhy9gu0': {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        vr8mkba7vtqr: {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية',
          },
          type: 'interaction',
        },
        bl1x9jj8jecr: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        sl5w76z5a23v: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        l54bmp5vf4am: {
          label: {
            en: 'Boot',
            ar: 'صندوق الأمتعة',
          },
          type: 'interaction',
        },
        '298irexzk84w': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        wh1frmtr0u28: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        q9ddyvv56xva: {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية',
          },
          type: 'interaction',
        },
        ahiduitywbma: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
        p6oclm23ki5e: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '54kqwzjme0so': {
          label: {
            en: 'Boot',
            ar: 'صندوق الأمتعة',
          },
          type: 'interaction',
        },
        '7xfwfti9azof': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        gj4u0vlgz20o: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '7hs9183l5tlb': {
          label: {
            en: 'DRL',
            ar: 'مصابيح القيادة النهارية',
          },
          type: 'interaction',
        },
        af0dl2x747nl: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقاعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقاعد الصف الثاني',
          },
        },
        interiorCamera_2: {
          title: {
            en: 'Third Row Seat',
            ar: 'مقاعد الصف الثالث',
          },
        },
      },
      name: {
        en: 'Cadillac Escalade',
        ar: 'كاديلاك إسكاليد',
      },
      specifications: {
        heading: {
          en: 'Cadillac Escalade',
          ar: 'كاديلاك إسكاليد',
        },
        trim: {
          en: 'Luxury',
          ar: 'Luxury',
        },
        price: {
          en: '',
          ar: '',
        },
        model: 'Cadillac Escalade',
        seatingCapacity: '7/8 Seats',
        dimensions: {
          length: '5383 mm',
          width: '',
          height: '1st Row - 1074mm, 2nd Row -988mm and 3rd Row - 970mm',
          wheelbase: '3071 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: 'Up to 682 HP',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '623.7 NM',
            unit: '',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '6.2L',
            unit: '',
            label: {
              en: 'V8 Engine',
              ar: 'محرك V8',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'CADILLAC ESCALADE',
              ar: 'كاديلاك ESCALADE',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Intuitive Infotainment',
              ar: 'نظام معلومات وترفيه متطور',
            },
            subtitle: {
              en: '55" diagonal HD curved front display with navigation',
              ar: 'شاشة HD أمامية منحنية قياس 55 بوصة مع نظام الملاحة',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Black Raven, Vibrant White Tricoat, Argent Silver Metallic, Radiant Red Tintcoat, Aegean Stone, Galactic Gray Metallic',
              ar: 'أسود Raven، أبيض Vibrant بثلاث طبقات، فضي Argent معدني، أحمر ساطع بطبقتين، Aegean Stone، رمادي Galactic معدني',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الألوان الداخلية',
            },
            subtitle: {
              en: 'Jet Black, Whisper Beige, Brandy, Renaissance Red, Sheer Gray',
              ar: '‎،Jet Black ،Whisper Beige ،Brandy Renaissance Red ،Sheer Gray',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3071 mm',
              ar: '3071 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Total Length',
              ar: 'الطول الإجمالي',
            },
            subtitle: {
              en: '5383 mm',
              ar: '5383 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Headroom',
              ar: 'حيّز الرأس',
            },
            subtitle: {
              en: '1st Row - 1074mm, 2nd Row -988mm and 3rd Row - 970mm',
              ar: 'الصف الأول: 1074 مم، الصف الثاني: 988 مم، الصف الثالث: 970 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Legroom',
              ar: 'حيّز الأرجل',
            },
            subtitle: {
              en: '1st Row - 1131mm, 2nd Row -1060mm and 3rd Row - 886mm',
              ar: 'الصف الأول: 1131 مم، الصف الثاني: 1060 مم، الصف الثالث: 886 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo volume',
              ar: 'حجم الحمولة',
            },
            subtitle: {
              en: '2064.5 Liter',
              ar: '2064.5 لتراً',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'نظام الدفع',
            },
            subtitle: {
              en: '6.2L Engine',
              ar: 'محرك سعة 6.2 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد المقاعد',
            },
            subtitle: {
              en: '7/8 Seats',
              ar: '7 أو 8 مقاعد',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.cadillacarabia.com/ae-en/privacy-statement',
        ar: 'https://www.cadillacarabia.com/ae-ar/privacy-statement',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Cadillac Escalade configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my Cadillac Escalade configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My Cadillac Escalade Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom Cadillac Escalade configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my Cadillac Escalade Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream Cadillac Escalade. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: '',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: '',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: '',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: '',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: '',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
      {
        id: 5,
        country: 'KSA',
        value: '',
        dealerId: '318928',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - King Fahad Road, Dammam',
          ar: 'الجميح للسيارات - طريق الملك فهد، الدمام',
        },
      },
      {
        id: 6,
        country: 'KSA',
        value: '',
        dealerId: '213142',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Rawdah Khurais Road, Riyadh',
          ar: 'الجميح للسيارات - طريق الروضة خريص، الرياض',
        },
      },
      {
        id: 7,
        country: 'KSA',
        value: '',
        dealerId: '307267',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Malibari Al Madina Road, Jeddah',
          ar: 'الجميح للسيارات - طريق ماليباري المدينة، جدة',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: '',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: '',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '259952',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    showPrices: false,
    showDisplayCarPrice: false,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'escalade',
    // Exact model name for the lead file's Model column.
    leadModelName: 'Escalade',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Escalade',
    modelName: 'Escalade',
    pageTitle: 'Escalade Configurator',
    client: 'GM',
    brand: 'cadillac',
    modelId: 'CAESC2025',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'cadillac',
      modelName: 'escalade',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2383/presentation_video_1.webm',
      loadingImage: 'images/2383/sliderImage.webp',
      transitionImage: 'images/2383/transition_logo.png',
      headerLogo: 'images/2383/transition_logo.png',
      headerLogoKeepColor: true,
      headerLogoInvertInLight: true,
      // Cadillac's crest reads too small at the shared default header-logo size.
      headerLogoLarge: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: 'Up to 682 HP',
            ar: 'حتى 682 حصاناً',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '623.7 NM',
            ar: '623.7 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'V8 Engine',
            ar: 'محرك V8',
          },
          value: {
            en: '6.2L',
            ar: '6.2 لتر',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; only the booking forms require it.
        requiredFor: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Cadillac Gothic\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Cadillac Gothic\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#1a1971',
      '--brand-primary-hover': '#15146a',
      '--brand-secondary': '#191897',
      '--brand-accent': '#1a1971',
      '--brand-accent-hover': '#15146a',
      '--brand-accent-light': '#e8e8f0',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#1a1971',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#1a1971',
      '--brand-toggle-gradient-to': '#191897',
      '--brand-toggle-knob-border': '#1a1971',
      '--brand-accent-secondary': '#1a1971',
      '--brand-card-radius': '0px',
      '--brand-card-radius-sm': '0px',
      '--brand-card-radius-lg': '0px',
      '--brand-button-radius': '0px',
      '--brand-button-radius-sm': '0px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '0px',
      // Enter-CTA fill: white with the Cadillac blue chevron, rather than
      // the default #0072ce block. Shadow keeps the label legible on it.
      '--brand-cta-arrow-bg': '#ffffff',
      '--brand-cta-arrow-fg': '#1a1971',
      '--brand-cta-shimmer': '#ffffff',
      '--enter-configurator-text-shadow': '0px 0px 12px #000000',
      '--brand-button-border': '#ffffff',
      '--brand-dialog-radius': '0px',
      '--brand-dialog-option-radius': '0px',
    },
  },
} as unknown as UIMetadataComplete;
